import * as api from '@api';
import * as constants from '@constants';
import { useAuth } from '@state/auth';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const useTranslator = props => {
  const { me, meReceived } = useAuth(state => ({
    me: state.me,
    meReceived: state.meReceived,
  }));

  const { t, i18n } = useTranslation(props?.page || null);

  useEffect(() => {
    if (!me) return;
    const language = me.preferences?.userLanguage?.language;
    if (language !== i18n.language && language != null) {
      i18n.changeLanguage(language);
    }
  }, [me]);

  // Memoized so its identity is stable across renders — consumers that list
  // `getTranslation` in effect dependency arrays (e.g. header/footer override
  // effects) would otherwise re-run every render and loop.
  const getTranslation = useCallback(
    (key, placeholderValues) => {
      //We want to ignore all translations and use the English translation instead for bulk imports modals
      // Array of keywords to check against
      const keywordsToIgnore = [
        'bulkImport',
        'uploadPurchaseOrderModal',
        'UploadSalesOrderModal',
      ];

      // Check if the key contains any of the keywords
      const shouldUseDefaultLanguage = keywordsToIgnore.some(keyword =>
        key.includes(keyword),
      );

      // If the key contains any of the keywords, return the English translation
      if (shouldUseDefaultLanguage) {
        return String(t(key, { lng: 'en', ...placeholderValues }));
      }

      return String(t(key, placeholderValues));
    },
    [t],
  );

  const switchLanguage = language => {
    i18n.changeLanguage(language);
    api.backend
      .mePreferencesPatch([
        {
          key: constants.USER_PREFERENCE_KEY_ENUMS.USERS_LANGUAGE,
          value: { language: language },
        },
      ])
      .then(response => {
        if (response.status === 200) {
          meReceived({ ...me, preferences: response.data });
        }
      });
  };

  const getCurrentLanguage = () => {
    return i18n.language;
  };

  return { getTranslation, switchLanguage, getCurrentLanguage, i18n };
};

export default useTranslator;
